import { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { formatDistanceToNow } from 'date-fns';
import { useOutlet } from '../contexts/OutletContext';
import { subscribeToOutletDevices } from '../services/outletService';
import { MdDevices, MdCheckCircle, MdError, MdRefresh, MdSearch, MdTerminal, MdDescription, MdHistory, MdCloudOff } from 'react-icons/md';
import { FiActivity } from 'react-icons/fi';
import { doc, setDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';

const commandTypes = [
  { value: 'CommandType.lockDevice', label: 'Lock Device' },
  { value: 'CommandType.unlockDevice', label: 'Unlock Device' },
  { value: 'CommandType.updatePolicy', label: 'Update Policy' },
  { value: 'CommandType.forceSync', label: 'Force Sync' },
  { value: 'CommandType.updateApp', label: 'Update App' },
  { value: 'CommandType.reboot', label: 'Reboot Device' },
  { value: 'CommandType.wipeData', label: 'Wipe Data (Danger!)' },
  { value: 'CommandType.requestLogs', label: 'Request Logs' },
  { value: 'CommandType.enableFeature', label: 'Enable Feature (Custom)', needsParams: true },
  { value: 'CommandType.disableFeature', label: 'Disable Feature (Custom)', needsParams: true },
  { 
    id: 'enableKioskMode',
    value: 'CommandType.enableFeature', 
    label: '🔒 Enable Kiosk Mode',
    autoParams: { feature: 'kioskMode' }
  },
  { 
    id: 'disableKioskMode',
    value: 'CommandType.disableFeature', 
    label: '🔓 Disable Kiosk Mode',
    autoParams: { feature: 'kioskMode' }
  },
  { 
    id: 'setKioskPin',
    value: 'CommandType.setKioskExitPin', 
    label: '🔐 Set Kiosk Exit PIN',
    needsParams: true
  },
  { 
    id: 'enableAutoStart',
    value: 'CommandType.setAutoStart', 
    label: '🚀 Enable Auto-Start on Boot',
    autoParams: { enable: true }
  },
  { 
    id: 'disableAutoStart',
    value: 'CommandType.setAutoStart', 
    label: '🛑 Disable Auto-Start on Boot',
    autoParams: { enable: false }
  },
];

function DevicesView() {
  const { selectedOutlet } = useOutlet();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCommandModal, setShowCommandModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [newCommand, setNewCommand] = useState({
    selectedCommandId: '',
    type: '',
    parameters: '',
  });
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [deviceLogs, setDeviceLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (!selectedOutlet) {
      console.log('[DevicesView] No outlet selected');
      setLoading(false);
      return;
    }

    console.log('[DevicesView] Subscribing to devices for outlet:', selectedOutlet.id);
    setLoading(true);

    const unsubscribe = subscribeToOutletDevices(
      selectedOutlet.id,
      (deviceList) => {
        console.log('[DevicesView] Received devices:', deviceList.length);
        const sortedDevices = deviceList.sort((a, b) => {
          const aTime = a.lastOnlineAt?.toDate ? a.lastOnlineAt.toDate().getTime() : 0;
          const bTime = b.lastOnlineAt?.toDate ? b.lastOnlineAt.toDate().getTime() : 0;
          return bTime - aTime;
        });
        setDevices(sortedDevices);
        setLoading(false);
        setError(null);
      }
    );

    return () => unsubscribe();
  }, [selectedOutlet]);

  const handleRefresh = () => {
    setLoading(true);
    setTimeout(() => setLoading(false), 1000);
  };

  const handleOpenCommandModal = (device) => {
    setSelectedDevice(device);
    setNewCommand({
      selectedCommandId: '',
      type: '',
      parameters: '',
    });
    setShowCommandModal(true);
  };

  const handleCloseCommandModal = () => {
    setShowCommandModal(false);
    setSelectedDevice(null);
    setNewCommand({
      selectedCommandId: '',
      type: '',
      parameters: '',
    });
  };

  const handleSendCommand = async () => {
    if (!newCommand.type || !selectedDevice) {
      alert('Please select a command');
      return;
    }

    try {
      let parameters = {};
      if (newCommand.parameters) {
        try {
          parameters = JSON.parse(newCommand.parameters);
        } catch (e) {
          alert('Invalid JSON in parameters field');
          return;
        }
      }

      const commandId = `cmd_${Date.now()}`;
      await setDoc(doc(db, 'remote_commands', commandId), {
        id: commandId,
        type: newCommand.type,
        targetTerminalId: selectedDevice.id,
        parameters: parameters || null,
        status: 'CommandStatus.pending',
        createdAt: new Date().toISOString(),
        issuedBy: 'admin',
        executedAt: null,
        result: null,
        error: null,
      });

      alert(`Command sent to ${selectedDevice.terminalId || selectedDevice.id}`);
      handleCloseCommandModal();
    } catch (err) {
      console.error('Error sending command:', err);
      alert('Error sending command: ' + err.message);
    }
  };

  const handleOpenLogsModal = async (device) => {
    setSelectedDevice(device);
    setShowLogsModal(true);
    setLogsLoading(true);
    setDeviceLogs([]);

    // Subscribe to logs for this device
    const q = query(
      collection(db, 'terminal_logs'),
      where('terminalId', '==', device.terminalId)
    );

    onSnapshot(
      q,
      (querySnapshot) => {
        const logs = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setDeviceLogs(logs);
        setLogsLoading(false);
      },
      (error) => {
        console.error('Error fetching logs:', error);
        setLogsLoading(false);
      }
    );
  };

  const handleCloseLogsModal = () => {
    setShowLogsModal(false);
    setSelectedDevice(null);
    setDeviceLogs([]);
  };

  const getLevelBadge = (level) => {
    const levelColors = {
      DEBUG: 'bg-secondary',
      INFO: 'bg-info',
      VERBOSE: 'bg-secondary',
      WARNING: 'bg-warning',
      ERROR: 'bg-danger',
      FATAL: 'bg-danger',
    };

    return (
      <span className={`badge rounded-pill ${levelColors[level] || 'bg-secondary'}`} style={{ minWidth: '80px' }}>
        {level}
      </span>
    );
  };

  const toDate = (timestamp) => {
    if (!timestamp) return null;
    if (timestamp.toDate) return timestamp.toDate();
    if (timestamp instanceof Date) return timestamp;
    if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      return new Date(timestamp);
    }
    return null;
  };

  const handleOpenHistoryModal = async (device) => {
    setSelectedDevice(device);
    setShowHistoryModal(true);
    setHistoryLoading(true);
    setCommandHistory([]);

    // Subscribe to command history for this device
    const q = query(
      collection(db, 'remote_commands'),
      where('targetTerminalId', '==', device.id)
    );

    onSnapshot(
      q,
      (querySnapshot) => {
        const commands = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        // Sort by createdAt descending (newest first)
        commands.sort((a, b) => {
          const aTime = toDate(a.createdAt)?.getTime() || 0;
          const bTime = toDate(b.createdAt)?.getTime() || 0;
          return bTime - aTime;
        });
        setCommandHistory(commands);
        setHistoryLoading(false);
      },
      (error) => {
        console.error('Error fetching command history:', error);
        setHistoryLoading(false);
      }
    );
  };

  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setSelectedDevice(null);
    setCommandHistory([]);
  };

  const getCommandStatusBadge = (status) => {
    const statusColors = {
      'CommandStatus.pending': 'bg-warning',
      'pending': 'bg-warning',
      'CommandStatus.completed': 'bg-success',
      'completed': 'bg-success',
      'CommandStatus.failed': 'bg-danger',
      'failed': 'bg-danger',
      'CommandStatus.executing': 'bg-info',
      'executing': 'bg-info',
    };

    const statusLabels = {
      'CommandStatus.pending': 'Pending',
      'pending': 'Pending',
      'CommandStatus.completed': 'Completed',
      'completed': 'Completed',
      'CommandStatus.failed': 'Failed',
      'failed': 'Failed',
      'CommandStatus.executing': 'Executing',
      'executing': 'Executing',
    };

    return (
      <span className={`badge rounded-pill ${statusColors[status] || 'bg-secondary'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  const filteredDevices = devices.filter(
    (device) =>
      device.terminalId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.deviceModel?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.outletId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (lastOnlineAt) => {
    if (!lastOnlineAt) return <span className="badge rounded-pill bg-secondary">Unknown</span>;
    
    const timestamp = lastOnlineAt.toDate ? lastOnlineAt.toDate() : new Date(lastOnlineAt);
    const minutesAgo = (Date.now() - timestamp.getTime()) / (1000 * 60);
    
    if (minutesAgo < 5) {
      return <span className="badge rounded-pill bg-success">Online</span>;
    } else if (minutesAgo < 30) {
      return <span className="badge rounded-pill bg-warning">Recent</span>;
    } else {
      return <span className="badge rounded-pill bg-danger">Offline</span>;
    }
  };

  const formatLastSeen = (lastOnlineAt) => {
    if (!lastOnlineAt) return 'Never';
    const timestamp = lastOnlineAt.toDate ? lastOnlineAt.toDate() : new Date(lastOnlineAt);
    return formatDistanceToNow(timestamp, { addSuffix: true });
  };

  const onlineCount = devices.filter(d => {
    if (!d.lastOnlineAt) return false;
    const timestamp = d.lastOnlineAt.toDate ? d.lastOnlineAt.toDate() : new Date(d.lastOnlineAt);
    const minutesAgo = (Date.now() - timestamp.getTime()) / (1000 * 60);
    return minutesAgo < 5;
  }).length;

  const offlineCount = devices.length - onlineCount;

  const compliantCount = devices.filter((d) => d.isCompliant).length;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <MdError className="me-2" />
        Error loading devices: {error}
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0 fw-bold">
          <MdDevices className="me-2" />
          Devices
        </h2>
        <button className="btn btn-outline-primary" onClick={handleRefresh}>
          <MdRefresh className={loading ? 'spinner-border spinner-border-sm' : ''} />
          <span className="ms-2">Refresh</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="row g-3 mb-4">
        <div className="col-lg col-md-4 col-sm-6">
          <div className="card border-0 shadow-sm h-100 rounded-3">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-primary bg-gradient rounded-pill p-3">
                    <MdDevices size={32} className="text-white" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h6 className="text-muted mb-1">Total Devices</h6>
                  <h3 className="mb-0">{devices.length}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg col-md-4 col-sm-6">
          <div className="card border-0 shadow-sm h-100 rounded-3">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-success bg-gradient rounded-pill p-3">
                    <FiActivity size={32} className="text-white" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h6 className="text-muted mb-1">Online</h6>
                  <h3 className="mb-0 text-success">{onlineCount}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg col-md-4 col-sm-6">
          <div className="card border-0 shadow-sm h-100 rounded-3">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-secondary bg-gradient rounded-pill p-3">
                    <MdCloudOff size={32} className="text-white" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h6 className="text-muted mb-1">Offline</h6>
                  <h3 className="mb-0 text-secondary">{offlineCount}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg col-md-4 col-sm-6">
          <div className="card border-0 shadow-sm h-100 rounded-3">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-success bg-gradient rounded-pill p-3">
                    <MdCheckCircle size={32} className="text-white" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h6 className="text-muted mb-1">Compliant</h6>
                  <h3 className="mb-0 text-success">{compliantCount}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg col-md-4 col-sm-6">
          <div className="card border-0 shadow-sm h-100 rounded-3">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-danger bg-gradient rounded-pill p-3">
                    <MdError size={32} className="text-white" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h6 className="text-muted mb-1">Non-Compliant</h6>
                  <h3 className="mb-0 text-danger">{devices.length - compliantCount}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Devices Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body">
          {/* Search */}
          <div className="input-group mb-3">
            <span className="input-group-text">
              <MdSearch />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by terminal ID, model, or outlet..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="table table-hover table-bordered">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '60px' }}></th>
                  <th>Terminal ID</th>
                  <th>Status</th>
                  <th>Device Model</th>
                  <th>OS Version</th>
                  <th>Battery</th>
                  <th>Storage</th>
                  <th>Compliance</th>
                  <th>Last Seen</th>
                </tr>
              </thead>
              <tbody>
                {filteredDevices.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center text-muted py-4">
                      No devices found
                    </td>
                  </tr>
                ) : (
                  filteredDevices.map((device) => (
                    <tr key={device.id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <MdTerminal 
                            size={24}
                            className="text-primary"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleOpenCommandModal(device)}
                            title="Send Command"
                          />
                          <MdDescription 
                            size={24}
                            className="text-success"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleOpenLogsModal(device)}
                            title="View Logs"
                          />
                          <MdHistory 
                            size={24}
                            className="text-info"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleOpenHistoryModal(device)}
                            title="Command History"
                          />
                        </div>
                      </td>
                      <td>
                        <strong>{device.terminalId || 'N/A'}</strong>
                      </td>
                      <td>{getStatusBadge(device.lastOnlineAt)}</td>
                      <td>{device.deviceModel || 'Unknown'}</td>
                      <td>{device.osVersion || 'Unknown'}</td>
                      <td>
                        {device.batteryLevel ? (
                          <div>
                            <small className="text-muted">{device.batteryLevel}%</small>
                            <div className="progress" style={{ height: '4px' }}>
                              <div 
                                className={`progress-bar ${device.batteryLevel < 20 ? 'bg-danger' : device.batteryLevel < 50 ? 'bg-warning' : 'bg-success'}`}
                                style={{ width: `${device.batteryLevel}%` }}
                              ></div>
                            </div>
                          </div>
                        ) : 'N/A'}
                      </td>
                      <td>
                        {device.storageUsedPercent ? (
                          <div>
                            <small className="text-muted">{device.storageUsedPercent.toFixed(1)}%</small>
                            <div className="progress" style={{ height: '4px' }}>
                              <div 
                                className={`progress-bar ${device.storageUsedPercent > 80 ? 'bg-danger' : device.storageUsedPercent > 60 ? 'bg-warning' : 'bg-info'}`}
                                style={{ width: `${device.storageUsedPercent}%` }}
                              ></div>
                            </div>
                          </div>
                        ) : 'N/A'}
                      </td>
                      <td>
                        {device.isCompliant ? (
                          <span className="badge rounded-pill bg-success">Compliant</span>
                        ) : (
                          <span className="badge rounded-pill bg-danger">Non-Compliant</span>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">{formatLastSeen(device.lastOnlineAt)}</small>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Command Modal */}
      <Modal show={showCommandModal} onHide={handleCloseCommandModal}>
        <Modal.Header closeButton>
          <Modal.Title>Commands</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Command Type *</label>
            <select
              className="form-select"
              value={newCommand.selectedCommandId}
              onChange={(e) => {
                const selectedCommand = commandTypes.find(
                  cmd => (cmd.id || cmd.value) === e.target.value
                );
                const autoParams = selectedCommand?.autoParams;
                
                setNewCommand({ 
                  ...newCommand,
                  selectedCommandId: e.target.value,
                  type: selectedCommand?.value || e.target.value,
                  parameters: autoParams ? JSON.stringify(autoParams, null, 2) : newCommand.parameters
                });
              }}
              required
            >
              <option value="">Select a command...</option>
              {commandTypes.map((cmd) => (
                <option key={cmd.id || cmd.value} value={cmd.id || cmd.value}>
                  {cmd.label}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-3">
            <label className="form-label">Parameters (JSON)</label>
            <textarea
              className="form-control font-monospace"
              value={newCommand.parameters}
              onChange={(e) => setNewCommand({ ...newCommand, parameters: e.target.value })}
              rows={4}
              placeholder='Optional JSON parameters, e.g., {"key": "value"}'
            />
          </div>

          <div className="alert alert-warning">
            <strong>Warning:</strong> Some commands like WIPE_DATA are irreversible.
            Make sure you have selected the correct device.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCommandModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSendCommand}>
            Send Command
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Command History Modal */}
      <Modal show={showHistoryModal} onHide={handleCloseHistoryModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Command History - {selectedDevice?.terminalId}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {historyLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : commandHistory.length === 0 ? (
            <div className="alert alert-info">
              No command history available for this device.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover table-bordered">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '180px' }}>Created At</th>
                    <th>Command Type</th>
                    <th style={{ width: '120px' }}>Status</th>
                    <th>Parameters</th>
                    <th>Issued By</th>
                    <th style={{ width: '180px' }}>Executed At</th>
                  </tr>
                </thead>
                <tbody>
                  {commandHistory.map((cmd) => (
                    <tr key={cmd.id}>
                      <td>
                        <small>
                          {toDate(cmd.createdAt)
                            ? formatDistanceToNow(toDate(cmd.createdAt), { addSuffix: true })
                            : 'N/A'}
                        </small>
                      </td>
                      <td>
                        <code className="small">{cmd.type || 'N/A'}</code>
                      </td>
                      <td>{getCommandStatusBadge(cmd.status)}</td>
                      <td>
                        {cmd.parameters ? (
                          <code className="small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {JSON.stringify(cmd.parameters, null, 2)}
                          </code>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <small>{cmd.issuedBy || cmd.createdBy || 'N/A'}</small>
                      </td>
                      <td>
                        <small>
                          {cmd.executedAt
                            ? toDate(cmd.executedAt)
                              ? formatDistanceToNow(toDate(cmd.executedAt), { addSuffix: true })
                              : 'N/A'
                            : '-'}
                        </small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseHistoryModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Logs Modal */}
      <Modal show={showLogsModal} onHide={handleCloseLogsModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Logs - {selectedDevice?.terminalId}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {logsLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : deviceLogs.length === 0 ? (
            <div className="alert alert-info">
              No logs available for this device. Send a "Request Logs" command to collect logs.
            </div>
          ) : (
            deviceLogs.map((terminalLog) => (
              <div key={terminalLog.id} className="mb-3">
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">
                    Uploaded{' '}
                    {toDate(terminalLog.uploadedAt)
                      ? formatDistanceToNow(toDate(terminalLog.uploadedAt), { addSuffix: true })
                      : 'N/A'}{' '}
                    ({terminalLog.logCount || 0} logs)
                  </span>
                </div>
                {terminalLog.logs && terminalLog.logs.length > 0 ? (
                  <div className="table-responsive" style={{ maxHeight: '500px', overflow: 'auto' }}>
                    <table className="table table-sm table-hover">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th style={{ width: '150px' }}>Timestamp</th>
                          <th style={{ width: '100px' }}>Level</th>
                          <th style={{ width: '200px' }}>Tag</th>
                          <th>Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {terminalLog.logs.map((log, logIndex) => (
                          <tr
                            key={logIndex}
                            className={
                              log.level === 'ERROR' || log.level === 'FATAL'
                                ? 'table-danger'
                                : log.level === 'WARNING'
                                ? 'table-warning'
                                : ''
                            }
                          >
                            <td>
                              <code className="small">{log.timestamp}</code>
                            </td>
                            <td>{getLevelBadge(log.level)}</td>
                            <td>
                              <code className="small">{log.tag}</code>
                            </td>
                            <td>
                              <code className="small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {log.message}
                              </code>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="alert alert-warning">No log entries found.</div>
                )}
              </div>
            ))
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseLogsModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default DevicesView;
